import i18n from "i18next";
import { Button } from "@/components/lilia-ui/button";
import { Spinner } from "@/components/lilia-ui/spinner";
import { IconX, IconCheck, IconCircleCheck, IconLanguage } from "@tabler/icons-react";
import { cn } from "@/lib/cn";

interface GrammarSuggestion {
  original: string;
  suggestion: string;
  type: "grammar" | "style" | "clarity" | "passive_voice";
  explanation: string;
}

interface GrammarPanelProps {
  isOpen: boolean;
  isLoading: boolean;
  suggestions: GrammarSuggestion[];
  error: string | null;
  onClose: () => void;
  onApply: (index: number) => void;
  onDismiss: (index: number) => void;
}

const TYPE_LABELS: Record<GrammarSuggestion["type"], string> = {
  grammar: "Grammar",
  style: "Style",
  clarity: "Clarity",
  passive_voice: "Passive Voice",
};

const TYPE_COLORS: Record<GrammarSuggestion["type"], string> = {
  grammar: "bg-red-500/10 text-red-600",
  style: "bg-violet-500/10 text-violet-600",
  clarity: "bg-amber-500/10 text-amber-600",
  passive_voice: "bg-sky-500/10 text-sky-600",
};

export function GrammarPanel({
  isOpen,
  isLoading,
  suggestions,
  error,
  onClose,
  onApply,
  onDismiss,
}: GrammarPanelProps) {
  if (!isOpen) return null;

  return (
    <div data-testid="grammar-panel" className="fixed right-0 top-0 bottom-0 w-[380px] bg-[var(--lilia-surface)] border-l border-[var(--lilia-border)] shadow-lg flex flex-col z-[100] animate-in slide-in-from-right duration-200">
      <div className="flex items-center justify-between px-4 py-3 border-b border-[var(--lilia-border)]">
        <div className="flex items-center gap-2 font-semibold text-sm text-foreground">
          <IconLanguage size={18} />
          <span>{i18n.t("editor:grammar.grammarStyle")}</span>
        </div>
        <Button variant="subtle" size="icon-sm" onClick={onClose} title="Close (Esc)" data-testid="grammar-close">
          <IconX size={16} />
        </Button>
      </div>

      {isLoading && (
        <div className="flex items-center justify-center py-10 gap-2 text-muted-foreground text-[13px]">
          <Spinner size="sm" />
          <span>{i18n.t("editor:grammar.checkingGrammarAndStyle")}</span>
        </div>
      )}

      {error && <div data-testid="grammar-error" className="mx-4 mt-2 p-3 bg-red-500/10 text-red-600 rounded-md text-[13px]">{error}</div>}

      {!isLoading && !error && suggestions.length === 0 && (
        <div data-testid="grammar-none" className="flex flex-col items-center justify-center py-10 text-center text-muted-foreground text-[13px] gap-2">
          <IconCircleCheck size={32} />
          <span>{i18n.t("editor:grammar.noIssuesFoundYourWriting")}</span>
        </div>
      )}

      {!isLoading && suggestions.length > 0 && (
        <>
          <div className="px-4 py-2 text-xs text-muted-foreground border-b border-[var(--lilia-border)]">
            {suggestions.length} {suggestions.length === 1 ? "issue" : "issues"} found
          </div>
          <div className="flex-1 overflow-y-auto py-2">
            {suggestions.map((item, index) => (
              <div key={`${item.original}-${index}`} data-testid="grammar-suggestion" data-type={item.type} className="px-4 py-3 border-b border-[var(--lilia-border)] last:border-b-0">
                <span className={cn(
                  "inline-block px-2 py-0.5 rounded-full text-[11px] font-medium capitalize mb-1.5",
                  TYPE_COLORS[item.type]
                )}>
                  {TYPE_LABELS[item.type]}
                </span>
                <div className="text-[13px] text-muted-foreground line-through mb-1 leading-relaxed">{item.original}</div>
                <div className="text-[13px] text-foreground font-medium mb-1 leading-relaxed">{item.suggestion}</div>
                <div className="text-xs text-muted-foreground mb-2 leading-relaxed">{item.explanation}</div>
                <div className="flex gap-1.5">
                  <Button
                    size="sm"
                    data-testid="grammar-apply"
                    onClick={() => onApply(index)}
                  >
                    <IconCheck size={12} className="mr-1" />
                    {i18n.t("editor:grammar.apply")}
                  </Button>
                  <Button
                    size="sm"
                    variant="subtle"
                    data-testid="grammar-dismiss"
                    onClick={() => onDismiss(index)}
                  >
                    {i18n.t("editor:grammar.dismiss")}
                  </Button>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}
